import React, { useContext } from "react";
import { useRouter } from "next/router";
import { makeStyles } from "@material-ui/core/styles";
import { FormControl, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import { useSelector } from "react-redux";
import { CommonDataContext } from "../../../../context/CommonDataContext";
import { DEFAULT_CURRENCY_CODE } from "constants/variables";
import { setSelectedDeeplinkCurrencyChange } from "@/store/slices/booking";
import { useDispatch } from "react-redux";

const useStyles = makeStyles((theme) => ({
  menuPaper: {
    maxHeight: 250,
    [theme.breakpoints.down("xs")]: {
      transform: "translate(0%, 0%) !important"
    },
    [theme.breakpoints.up("sm")]: {
      transform: "translate(0%, 0%) !important"
    },
    [theme.breakpoints.up("md")]: {
      transform: "translate(-9%, 0%) !important"
    },
    [theme.breakpoints.up("lg")]: {
      transform: "translate(-34%, 0%) !important"
    }
  }
}));

type CurrencyCode = {
  description: string;
  currencyCode: string;
  displayPrefix: string;
};

interface CurrencyChangeListV2Props {
  selectedCurrency: string;
  setSelectedCurrency: (value: string) => void;
  filterCurrencies?: string[];
}

export function CurrencyChangeListV2({
  selectedCurrency,
  setSelectedCurrency,
  filterCurrencies
}: CurrencyChangeListV2Props) {

  const { availableCurrencies, webConfig }: any = useContext(CommonDataContext);
  const { asPath } = useRouter();
  const enableInternationalFlights = webConfig?.enableInternationalFlights;
  const enableFilteredCurrencies = webConfig?.enableFilteredCurrencies;
  const bookingInRedux = useSelector((state: any) => state.persistedReducers.booking);
  const isChangeCurrenciesAllow = enableInternationalFlights && (bookingInRedux.bookingType === "NEW_BOOKING" && bookingInRedux.progressStage === -1) || asPath === "/";;
  const classes = useStyles();
  const dispatch = useDispatch();

  const sortedCurrenciesData = availableCurrencies && availableCurrencies.values
    .sort((a: CurrencyCode, b: CurrencyCode) => {
      if (a.currencyCode === "INR") return -1;
      if (b.currencyCode === "INR") return 1;
      return a.currencyCode.localeCompare(b.currencyCode);
    });

  const renderCurrencies = (enableFilteredCurrencies && filterCurrencies)
    ? sortedCurrenciesData.filter((c: CurrencyCode) => filterCurrencies.some((fc) => c.currencyCode === fc))
    : sortedCurrenciesData;

  const renderCurrencyValue = (value: string) => {
    if (!renderCurrencies) {
      return "";
    }
    const selectedOption = renderCurrencies.find((currency: CurrencyCode) => currency?.currencyCode === value);
    if (selectedOption) {
      return `${selectedOption?.displayPrefix ? `${selectedOption?.displayPrefix} ` : ""} ${selectedOption?.currencyCode}`;
    }
    const inrOption = renderCurrencies.find((currency: CurrencyCode) => currency?.currencyCode === DEFAULT_CURRENCY_CODE);
    if (inrOption) {
      setSelectedCurrency(DEFAULT_CURRENCY_CODE);
      return `${inrOption?.displayPrefix ? `${inrOption?.displayPrefix} ` : ""} ${inrOption?.currencyCode}`;
    }
    return "";
  };

  const handleCurrencyChange = (event: SelectChangeEvent<any>) => {
    dispatch(setSelectedDeeplinkCurrencyChange(""))
    setSelectedCurrency(event.target.value);
  };

  if (isChangeCurrenciesAllow) {
    return (
      <div>
        <FormControl className="w-full">
          <Select
            className="font-black"
            id="currency-select"
            value={selectedCurrency}
            onChange={handleCurrencyChange}
            renderValue={(value) => renderCurrencyValue(value)}
            MenuProps={{ classes: { paper: classes.menuPaper } }}
            inputProps={{
              "aria-label":"Select Currency"
            }}
            sx={{
              "div#currency-select": {
                padding: "12px 32px 12px 8px"
              }
            }}
          >
            {renderCurrencies &&
              renderCurrencies.map((currency: CurrencyCode) => (
                <MenuItem style={{textAlign: "right" , display:"flex", padding:"6px 16px", fontSize:"1rem"}} key={currency?.currencyCode} value={currency?.currencyCode}>
                  <div className="flex justify-end w-full">
                    {`${currency?.description} - ${currency?.currencyCode} ${
                      currency?.displayPrefix ? `(${currency?.displayPrefix})` : ""
                    }`}
                  </div>
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </div>
    );
  }
  return <div />;
}
